import { AuthTokenBuilder } from '../utils/authTokenBuilder';
import { Logger } from '../utils/Logger';
import * as LogoutHelper from '../utils/LogoutHelper';

export const getFetchWithTimeout = (timeout, originalFetch) => {
  function generateReference() {
    if (typeof jest !== 'undefined') {
      return "0000000000000000";
    }

    // based on https://stackoverflow.com/a/105074/1016377
    function s4() {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    }
    return s4() + s4() + s4() + s4();
  }

  return async function fetchWithTimeout(url, options = {}) {
    try {
      options.handle401 = options.hasOwnProperty('handle401') ? options.handle401 : true

      const reference = generateReference();
      if (options.headers && options.headers.set) {
        options.headers.set('x-log-reference', reference);
      } else {
        options.headers = options.headers || {};
        options.headers['x-log-reference'] = reference;
      }

      const retryWithNewAuthToken = async (response, tries) => {
        if (tries > 0) {
          // max retries
          await LogoutHelper.default();
          return response;
        }

        const authTokenBuilder = new AuthTokenBuilder();
        const newHeader = await authTokenBuilder.forceRefreshAndGetAuthorizationHeader();
        options.headers.set('authorization', newHeader);

        return makeRequest(tries + 1);
      };

      function makeRequest(tries) {
        return new Promise(async (resolve, reject) => {
          const controller = new AbortController();
          const signal = controller.signal;
          options = options || {};
          options.signal = signal;

          try {
            setTimeout(() => {
              controller.abort();
            }, timeout);

            const response = await originalFetch(url, options);
            if (response.status === 401) {
              if (options.handle401) {
                const retryResult = await retryWithNewAuthToken(response, tries);
                resolve(retryResult);
                return;
              } else {
                return reject({ name: '401', message: '401 unauthorized' })
              }
            }
            response.logReference = reference;
            resolve(response);
          } catch (e) {
            try {
              e.logReference = reference;
              e.message = `${e.message} (reference: ${reference})`;
              logFailure(e, `failed to fetch for url ${url}` +
                `${controller.signal.aborted ? `,timeout after ${timeout}` : ''}`);
            } catch (err) { /* fails in jest tests? */ }
            reject(e);
          }
        });
      }
      return makeRequest(0);
    } catch (e) {
      logFailure(e, `failed to setup fetch for ${url}`);
      throw e;
    }
  }
}

function logFailure(e, message) {
  message += `error message: ${e.message}`;
  if (message.contains('http://10.0.2.2:8081/symbolicate')) {
    // ignore symbolication errors (goes in infinite loop)
    return;
  }

  Logger.notify(e, (report) => {
    report.errorClass = 'fetchWithTimeout';
    report.errorMessage = message;
  });
}

export function polyfillFetch(timeout, originalFetch) {
  fetch = getFetchWithTimeout(timeout, originalFetch);
}
